import { isNotNull } from "@latticexyz/common/utils";
import {
  camelToSpaces,
  formatPrice,
  getObjectImageUrl,
  objectTypes,
  truncateStr,
} from "@permutation-city/common";
import { useMemo } from "react";
import { useTerrainObjectTypesQuery } from "../terrain/useTerrainObjectTypesQuery";
import type { Blueprint } from "../useBlueprintQuery";
import { IncentiveAction, useIncentivesQuery } from "../useIncentivesQuery";

export function useBlueprintTableData(blueprint: Blueprint | undefined) {
  const { data: incentives } = useIncentivesQuery();
  const {
    data: terrainData,
    isLoading: isTerrainObjectTypesLoading,
    refetch: refetchTerrainObjectTypes,
  } = useTerrainObjectTypesQuery({
    blueprint: blueprint ?? [],
  });
  const { totalObjects, placedObjects } = terrainData ?? {};

  const blueprintItemsTableData = useMemo(() => {
    if (!blueprint) return [];

    const uniqueObjectTypes = [
      ...new Set(
        blueprint.map(
          ({ objectTypeId }: { objectTypeId: number }) => objectTypeId,
        ),
      ),
    ];

    const blueprintItemsTableData = uniqueObjectTypes
      .map((objectTypeId: number) => {
        // TODO: improve logic, ts
        let actionType = IncentiveAction.BuildBlueprint;
        // if "air", treat as mineable
        if (objectTypeId === 1) {
          actionType = IncentiveAction.MineBlueprint;
        }

        if (objectTypeId === 1 || objectTypeId === 0) {
          return null;
        }

        const incentive =
          incentives?.[actionType][objectTypeId] || incentives?.[actionType][0];

        return {
          id: objectTypeId,
          name: {
            value: truncateStr(
              camelToSpaces(objectTypes[objectTypeId]?.name).toUpperCase(),
            ),
            icon: getObjectImageUrl(objectTypeId),
          },
          reward: formatPrice({
            price: incentive?.tokenAmount,
            decimals: 2,
          }),
          reputation: formatPrice({
            price: incentive?.reputationAmount,
            decimals: 2,
          }),
          progress: isTerrainObjectTypesLoading
            ? "..."
            : `${placedObjects?.[objectTypeId] ?? 0} / ${totalObjects?.[objectTypeId] ?? 0}`,
        };
      })
      .filter(isNotNull);

    const mineIncentive = incentives?.[IncentiveAction.MineBlueprint][0];
    const airIncentive = incentives?.[IncentiveAction.MineBlueprint][1];

    return [
      {
        id: -1,
        name: {
          value: "SCAFFOLD (DIRT)",
          icon: getObjectImageUrl(22),
        },
        reward: `${formatPrice({
          price: incentives?.[IncentiveAction.BuildScaffold][0]?.tokenAmount,
          decimals: 2,
        })}`,
        reputation: formatPrice({
          price:
            incentives?.[IncentiveAction.BuildScaffold][0]?.reputationAmount,
          decimals: 2,
        }),
      },
      {
        id: 1,
        name: {
          value: "MINE",
          icon: getObjectImageUrl(32768),
        },
        reward: formatPrice({
          price: airIncentive?.tokenAmount ?? mineIncentive?.tokenAmount,
          decimals: 2,
        }),
        reputation: formatPrice({
          price:
            airIncentive?.reputationAmount ?? mineIncentive?.reputationAmount,
          decimals: 2,
        }),
      },
      {
        id: 0,
        name: {
          value: "ALL BLOCKS",
          icon: getObjectImageUrl(0),
        },
        reward: "",
      },
      ...blueprintItemsTableData,
    ];
  }, [
    blueprint,
    incentives,
    isTerrainObjectTypesLoading,
    placedObjects,
    totalObjects,
  ]);

  return {
    blueprintItemsTableData,
    isTerrainObjectTypesLoading,
    terrainData,
    isLoading: isTerrainObjectTypesLoading,
    refetchTerrainObjectTypes,
  };
}
